import {
    createRootRouteWithContext,
    HeadContent,
    Outlet,
} from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Toaster } from '@apps/web/components/ui/sonner';
import { type SessionStateContextType } from '@apps/web/context/session_state_provider';
import { useEmailClickTracking } from '@apps/web/hooks/use_email_click_tracking';
import { toast } from '@apps/web/lib/app_notifications';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import {
    isBenignBrowserErrorMessage,
    shouldToastUncaughtError,
} from '@apps/web/lib/sentry_tracing';
import { type User } from '@libraries/typescript/api_client';

const ERROR_TOAST_THROTTLE_MS = 60_000;
let lastErrorToastTimestamp = 0;

type MyRouterContext = {
    readonly sessionState: SessionStateContextType | null;
    readonly user: User | null;
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
    component: () => (
        <React.Fragment>
            <Root />
        </React.Fragment>
    ),
});

function Root() {
    useEmailClickTracking();

    useEffect(() => {
        const handleError = (event: ErrorEvent | PromiseRejectionEvent) => {
            let error;
            if ('reason' in event) {
                // PromiseRejectionEvent
                error = event.reason;
            } else {
                // ErrorEvent
                if (isBenignBrowserErrorMessage(event.message)) {
                    // Browsers can defer remaining ResizeObserver notifications
                    // to the next frame and report a harmless diagnostic.
                    event.preventDefault();
                    return;
                }
                error = event.error || new Error(event.message);
            }

            console.error('Unhandled Error:', error);

            if (!shouldToastUncaughtError(event)) {
                return;
            }

            const now = Date.now();
            if (now - lastErrorToastTimestamp >= ERROR_TOAST_THROTTLE_MS) {
                lastErrorToastTimestamp = now;
                const description =
                    getRunEnvironment() === 'development' &&
                    error instanceof Error
                        ? error.message
                        : 'If the problem persists, please contact support.';

                toast.error('An unexpected error occurred.', {
                    persistNotification: false,
                    description,
                    duration: 5000,
                });
            }
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleError);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleError);
        };
    }, []);

    return (
        <React.Fragment>
            {/* https://github.com/TanStack/router/discussions/1056#discussioncomment-12306354 */}
            {createPortal(<HeadContent />, document.querySelector('head')!)}
            <Outlet />
            <Toaster closeButton={true} position="top-right" />
        </React.Fragment>
    );
}
